import React from 'react';
import {
  Control,
  Controller,
  FieldName,
  FieldValues,
  UseFormMethods,
  RegisterOptions,
} from 'react-hook-form';
import RcSlider, { SliderProps } from 'rc-slider';
import { SliderRef } from 'rc-slider/lib/Slider';
import classNames from 'classnames';
import { FlattenKeys } from '../../../types/utilTypes';
import styles from './styles.module.scss';

const RcSingleSlider = RcSlider as React.ForwardRefExoticComponent<
  SliderProps<number> & React.RefAttributes<SliderRef>
>;

type Props<T extends FieldValues> = {
  className?: string;
  label: string;
  disabled?: boolean;
  max: number;
  min: number;
  marks?: SliderProps['marks'];
} & (
  | {
      // name is required when form is set
      name: FlattenKeys<T>;
      form: UseFormMethods<T>;
      validation?: RegisterOptions;
      // the form should control the component when it's set
      value?: undefined;
      onChange?: undefined;
    }
  | {
      name?: string;
      form?: undefined;
      // validation requires a form to be provided
      validation?: undefined;
      value?: number;
      onChange?: (value: number) => void;
    }
);

export const Slider = <T extends FieldValues>({
  className,
  label,
  disabled,
  max,
  min,
  marks,
  name,
  form,
  value,
  onChange,
}: Props<T>) => {
  const currentValue =
    value ?? ((form?.watch(name as FieldName<T>) as number | undefined) || 0);

  const inputProps = {
    name,
    min,
    max,
    marks,
    disabled,
  };

  const renderInput = () => {
    if (form) {
      return (
        <Controller
          control={form.control as Control<Record<string, any>>}
          defaultValue=""
          name={name as string}
          render={(controllerProps) => (
            <RcSingleSlider
              {...inputProps}
              value={controllerProps.value}
              onChange={controllerProps.onChange}
              onBlur={controllerProps.onBlur}
            />
          )}
        />
      );
    }
    return <RcSingleSlider {...inputProps} value={value} onChange={onChange} />;
  };

  return (
    <div className={classNames(styles.container, className)}>
      <span className={styles.label}>{label}</span>
      <div className={styles.inputContainer}>
        {renderInput()}
        <div className={styles.valueDisplay}>{currentValue}</div>
      </div>
    </div>
  );
};
